import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Activity, AlertTriangle, BookOpen, CheckCircle2, ClipboardList, Copy, Database,
  Download, ExternalLink, Search, Shield,
} from 'lucide-react';
import { channelApi } from '@/config/api';

const readinessTone = {
  'Not Configured': 'bg-slate-50 text-slate-600 border-slate-200',
  'Configuration Incomplete': 'bg-amber-50 text-amber-800 border-amber-200',
  'Ready for Testing': 'bg-sky-50 text-sky-800 border-sky-200',
  Testing: 'bg-blue-50 text-blue-800 border-blue-200',
  Active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  Degraded: 'bg-orange-50 text-orange-800 border-orange-200',
  Error: 'bg-red-50 text-red-800 border-red-200',
  Disabled: 'bg-slate-100 text-slate-700 border-slate-300',
};

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'api-endpoints', label: 'API Endpoints' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'data-mapping', label: 'Data Mapping' },
  { id: 'synchronization', label: 'Synchronization' },
  { id: 'testing', label: 'Testing' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
  { id: 'go-live', label: 'Go-Live Checklist' },
];

function Badge({ value, map = readinessTone }) {
  return (
    <span className={`inline-flex rounded-md border px-2 py-1 text-[11px] font-bold ${map[value] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      {value || '—'}
    </span>
  );
}

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function CopyBtn({ text }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
      onClick={() => { try { navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text, null, 2)); } catch { /* ignore */ } }}
    >
      <Copy className="h-3 w-3" /> Copy
    </button>
  );
}

function CodeBlock({ value }) {
  if (value == null || value === '') {
    return <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-500">Not Available</div>;
  }
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div className="relative">
      <div className="absolute right-2 top-2"><CopyBtn text={text} /></div>
      <pre className="overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-50">{text}</pre>
    </div>
  );
}

function Callout({ tone = 'info', children }) {
  const styles = {
    info: 'border-sky-200 bg-sky-50 text-sky-900',
    warn: 'border-amber-200 bg-amber-50 text-amber-900',
    danger: 'border-red-200 bg-red-50 text-red-900',
  };
  return <div className={`rounded-lg border p-3 text-sm ${styles[tone]}`}>{children}</div>;
}

function Directory({ items, summary, platform, loading, onOpen }) {
  const [query, setQuery] = React.useState('');
  const [readiness, setReadiness] = React.useState('ALL');
  const [typeFilter, setTypeFilter] = React.useState('ALL');
  const [sort, setSort] = React.useState('code');

  const types = React.useMemo(() => {
    const set = new Set(items.map((i) => i.integrationType).filter(Boolean));
    return [...set].sort();
  }, [items]);

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    let rows = items.filter((item) => {
      const hay = [item.member?.code, item.member?.name, item.category, item.integrationType, item.readiness].join(' ').toLowerCase();
      if (needle && !hay.includes(needle)) return false;
      if (readiness !== 'ALL' && item.readiness !== readiness) return false;
      if (typeFilter !== 'ALL' && item.integrationType !== typeFilter) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      if (sort === 'products') return (b.totalIntegratedProducts || 0) - (a.totalIntegratedProducts || 0);
      if (sort === 'sync') return String(b.lastSyncAt || '').localeCompare(String(a.lastSyncAt || ''));
      if (sort === 'readiness') return String(a.readiness).localeCompare(String(b.readiness));
      return String(a.member?.code).localeCompare(String(b.member?.code));
    });
    return rows;
  }, [items, query, readiness, typeFilter, sort]);

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start gap-3">
          <BookOpen className="mt-1 h-5 w-5 text-teal-700" />
          <div>
            <h2 className="text-lg font-bold text-slate-900">{platform?.title || 'Member Integration Guide'}</h2>
            <p className="mt-1 text-sm text-slate-600">{platform?.purpose}</p>
            <p className="mt-1 text-[11px] text-slate-400">Versi {platform?.version || '—'} · Diperbarui {platform?.updatedAt || '—'}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {[
          { title: 'Member', value: summary?.members, Icon: Database },
          { title: 'Active', value: summary?.active, Icon: CheckCircle2 },
          { title: 'Not Configured', value: summary?.notConfigured, Icon: AlertTriangle },
          { title: 'Error', value: summary?.error, Icon: Activity },
        ].map((c) => (
          <div key={c.title} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{c.title}</div>
                <div className="mt-2 text-2xl font-bold text-slate-900">{loading ? '…' : (c.value ?? '—')}</div>
              </div>
              <c.Icon className="h-5 w-5 text-slate-400" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="relative md:col-span-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari Member…" />
        </div>
        <select className="h-10 rounded-lg border border-slate-200 px-3 text-sm" value={readiness} onChange={(e) => setReadiness(e.target.value)}>
          <option value="ALL">All Readiness</option>
          {Object.keys(readinessTone).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="h-10 rounded-lg border border-slate-200 px-3 text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="ALL">All Integration Types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="h-10 rounded-lg border border-slate-200 px-3 text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="code">Sort: Member Code</option>
          <option value="readiness">Sort: Readiness</option>
          <option value="products">Sort: Products</option>
          <option value="sync">Sort: Last Sync</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Auth</th>
              <th className="px-4 py-3">Products</th>
              <th className="px-4 py-3">Last Sync</th>
              <th className="px-4 py-3">Readiness</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.member.code} className="border-t border-slate-100">
                <td className="px-4 py-3">
                  <div className="font-bold text-slate-900">{row.member.code}</div>
                  <div className="text-xs text-slate-500">{row.member.name}</div>
                </td>
                <td className="px-4 py-3 text-xs">{row.category || '—'}</td>
                <td className="px-4 py-3 text-xs">{row.integrationType || '—'}</td>
                <td className="px-4 py-3 text-xs">{row.authenticationType || '—'}</td>
                <td className="px-4 py-3 font-semibold">{row.totalIntegratedProducts ?? 0}</td>
                <td className="px-4 py-3 text-xs">
                  <div>{fmtDate(row.lastSuccessfulSync || row.lastSyncAt)}</div>
                  <div className="text-slate-400">{row.lastSyncStatus || '—'}</div>
                  {row.lastSyncError && <div className="text-red-600 truncate max-w-[180px]" title={row.lastSyncError}>{row.lastSyncError}</div>}
                </td>
                <td className="px-4 py-3"><Badge value={row.readiness} /></td>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => onOpen(row.member.code)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">
                    View Guide
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500">{loading ? 'Memuat directory…' : 'Tidak ada Member sesuai filter.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GuideDetail({ code }) {
  const navigate = useNavigate();
  const [section, setSection] = React.useState('overview');
  const [guide, setGuide] = React.useState(null);
  const [meta, setMeta] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    setError('');
    channelApi.get(`/admin/integration-guides/${code}`)
      .then((res) => {
        setGuide(res.data?.data || null);
        setMeta(res.data?.meta || null);
      })
      .catch((e) => setError(e.response?.data?.message || e.message || 'Gagal memuat guide'))
      .finally(() => setLoading(false));
  }, [code]);

  const downloadProviderReference = async () => {
    const res = await channelApi.get(`/admin/integration-guides/${code}/reference-export`);
    const blob = new Blob([JSON.stringify(res.data?.data || res.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prime-provider-reference-${code}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Memuat guide {code}…</div>;
  if (error) return <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  if (!guide) return <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Guide tidak ditemukan.</div>;

  const d = guide.directory;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-5">
        <div>
          <button type="button" className="text-xs font-semibold text-teal-700" onClick={() => navigate('/admin/internal-tools/api-documentation?tab=guide')}>← Member Directory</button>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-slate-900">{d.member.code}</h2>
            <Badge value={d.readiness} />
            <span className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-bold text-slate-600">{d.integrationStatus}</span>
          </div>
          <p className="mt-1 text-sm text-slate-600">{d.member.name} · {d.category || '—'}</p>
          <p className="mt-1 text-[11px] text-slate-400">Sources: {meta?.sources?.technical} · {meta?.sources?.operational} · {meta?.sources?.editorial}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => navigate(`/admin/internal-tools/member-integration/${code}`)} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">Sync Control</button>
          <button type="button" onClick={() => navigate('/admin/internal-tools/api-documentation?panel=playground')} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">API Playground</button>
          <button type="button" onClick={downloadProviderReference} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">
            <Download className="h-4 w-4" /> Provider Reference Export (not PRIME POC)
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        <nav className="lg:col-span-3">
          <div className="sticky top-4 space-y-1 rounded-lg border border-slate-200 bg-white p-2">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                className={`block w-full rounded-md px-3 py-2 text-left text-sm font-semibold ${section === s.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        <div className="space-y-4 lg:col-span-9">
          {section === 'overview' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold text-slate-900">Overview</h3>
              <p className="text-sm text-slate-700">{guide.overview.purpose}</p>
              <div className="grid gap-3 md:grid-cols-2">
                {(guide.overview.direction || []).map((dir) => (
                  <div key={dir.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{dir.name}</div>
                    <p className="mt-1 text-sm text-slate-700">{dir.description}</p>
                    <ul className="mt-2 list-disc pl-4 text-xs text-slate-600">
                      {(dir.examples || []).map((ex) => <li key={ex}>{ex}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="text-sm text-slate-600">
                <div><span className="font-semibold">Categories:</span> {(guide.overview.supportedCategories || []).join(', ') || '—'}</div>
                <div><span className="font-semibold">Contacts:</span> {guide.overview.contacts?.apiOwner || '—'} / {guide.overview.contacts?.escalation || '—'}</div>
                <div><span className="font-semibold">Last verified:</span> {fmtDate(guide.overview.lastVerified)}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(guide.overview.relatedDocumentation || []).map((l) => (
                  <button key={l.label} type="button" className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-semibold" onClick={() => (String(l.href).startsWith('/') ? navigate(l.href) : null)}>
                    <ExternalLink className="h-3 w-3" /> {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {section === 'getting-started' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold">Getting Started</h3>
              {(guide.gettingStarted.notes || []).map((n) => <Callout key={n}>{n}</Callout>)}
              <ol className="space-y-3">
                {(guide.gettingStarted.steps || []).map((step, idx) => (
                  <li key={step.id} className="rounded-lg border border-slate-100 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-semibold text-slate-900">{idx + 1}. {step.title}</div>
                      <Badge value={step.completed ? 'Active' : 'Not Configured'} />
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{step.description}</p>
                    <p className="mt-1 text-[11px] text-slate-400">{step.evidence}</p>
                  </li>
                ))}
              </ol>
              <Callout tone="warn">Step hanya ditandai selesai jika ada evidence dari registry/ops — bukan checklist visual semata.</Callout>
            </div>
          )}

          {section === 'api-endpoints' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold">API Endpoints</h3>
              <Callout>{guide.apiEndpoints.note}</Callout>
              <h4 className="text-sm font-bold text-slate-800">PRIME APIs (Swagger / Playground)</h4>
              <div className="space-y-2">
                {(guide.apiEndpoints.primeApis || []).map((ep) => (
                  <div key={`${ep.method}-${ep.path}`} className="rounded-lg border border-slate-100 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">{ep.method}</span>
                      <code className="text-xs">{ep.path}</code>
                      {ep.mutating && <span className="text-[10px] font-bold text-amber-700">MUTATION</span>}
                    </div>
                    <p className="mt-1 text-xs text-slate-600">{ep.description}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button type="button" className="rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold" onClick={() => navigate('/admin/internal-tools/api-documentation')}>View in Swagger</button>
                      <button type="button" className="rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold" onClick={() => navigate('/admin/internal-tools/api-documentation?panel=playground')}>Open in Playground</button>
                      <CopyBtn text={`curl -X ${ep.method} '${ep.path}' -H 'Authorization: Bearer YOUR_BEARER_TOKEN'`} />
                    </div>
                  </div>
                ))}
              </div>
              <h4 className="text-sm font-bold text-slate-800">Member upstream (provider — bukan path PRIME)</h4>
              {(guide.apiEndpoints.memberUpstream || []).length === 0 && <div className="text-sm text-slate-500">Not Available / Not Implemented</div>}
              {(guide.apiEndpoints.memberUpstream || []).map((c) => (
                <div key={`${c.adapter}-${c.category}`} className="rounded-lg border border-slate-100 p-3 space-y-2">
                  <div className="text-sm font-semibold">{c.category} · {c.adapter} · {c.transport}</div>
                  {(c.endpoints || []).length === 0 && <div className="text-xs text-slate-500">Not Available</div>}
                  {(c.endpoints || []).map((ep) => (
                    <div key={`${ep.method}-${ep.path || ep.name}`} className="border-t border-slate-50 pt-2">
                      <div className="flex flex-wrap gap-2 text-xs"><span className="font-bold">{ep.method}</span><code>{ep.path || 'Not Available'}</code></div>
                      {ep.curl && <CodeBlock value={ep.curl} />}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {section === 'authentication' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold flex items-center gap-2"><Shield className="h-4 w-4" /> Authentication</h3>
              <Callout tone="warn">{guide.authentication.secretPolicy}</Callout>
              {(guide.authentication.methods || []).map((m) => (
                <div key={`${m.adapter}-${m.category}`} className="rounded-lg border border-slate-100 p-3">
                  <div className="font-semibold text-sm">{m.method}</div>
                  <div className="text-xs text-slate-500">{m.category} / {m.adapter}</div>
                  <ul className="mt-2 space-y-1 text-xs text-slate-600">
                    {(m.configKeys || []).map((k) => (
                      <li key={k.key}><code>{k.key}</code> — {k.status} {k.required ? '(required)' : ''} — {k.description}</li>
                    ))}
                  </ul>
                  <div className="mt-2 text-xs">Headers: {(m.headers || []).join(' · ') || '—'}</div>
                </div>
              ))}
              {!guide.authentication.methods?.length && <div className="text-sm text-slate-500">Not Configured</div>}
              <button type="button" className="rounded-md bg-teal-700 px-3 py-2 text-sm font-semibold text-white" onClick={() => navigate('/admin/internal-tools/api-documentation?panel=playground')}>
                Authorize di API Playground
              </button>
            </div>
          )}

          {section === 'data-mapping' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold">Data Mapping</h3>
              <Callout>
                Canonical: {guide.dataMapping.normalization?.identity}. Upsert: {guide.dataMapping.normalization?.upsert}
              </Callout>
              {(guide.dataMapping.memberNotes || guide.dataMapping.normalization?.memberNotes || []).map((n) => <Callout key={n} tone="info">{n}</Callout>)}
              {guide.dataMapping.empty && <Callout tone="warn">Mapping fields kosong pada adapter meta — dokumentasikan di adapter.meta.mappings (bukan dikarang di UI).</Callout>}
              {(guide.dataMapping.mappings || []).map((block) => (
                <div key={`${block.adapter}-${block.category}`}>
                  <div className="mb-2 text-sm font-semibold">{block.category} · {block.adapter}</div>
                  <div className="overflow-auto rounded-lg border border-slate-100">
                    <table className="w-full min-w-[720px] text-left text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Source</th>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2">Target PRIME</th>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2">Transform</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(block.fields || []).map((f, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-mono">{f.sourceField || '—'}</td>
                            <td className="px-3 py-2">{f.sourceDataType || '—'}</td>
                            <td className="px-3 py-2 font-mono">{f.targetField || '—'}</td>
                            <td className="px-3 py-2">{f.targetDataType || '—'}</td>
                            <td className="px-3 py-2">{f.transformationRule || '—'}</td>
                            <td className="px-3 py-2">{f.mappingStatus || '—'}</td>
                          </tr>
                        ))}
                        {!block.fields?.length && <tr><td colSpan={6} className="px-3 py-4 text-slate-500">No mapping fields documented</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {section === 'synchronization' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold">Synchronization</h3>
              <div className="grid gap-3 md:grid-cols-3 text-sm">
                <div className="rounded border border-slate-100 p-3"><div className="text-[11px] uppercase text-slate-500">Manual</div>{guide.synchronization.notes?.manual || '—'}</div>
                <div className="rounded border border-slate-100 p-3"><div className="text-[11px] uppercase text-slate-500">Scheduled</div>{guide.synchronization.notes?.scheduled || '—'}</div>
                <div className="rounded border border-slate-100 p-3"><div className="text-[11px] uppercase text-slate-500">Incremental</div>{guide.synchronization.notes?.incremental || '—'}</div>
              </div>
              <div className="text-sm text-slate-600">
                Last sync: {fmtDate(guide.synchronization.lastSyncAt)} · Success: {fmtDate(guide.synchronization.lastSuccessfulSync)} · Status: {guide.synchronization.lastSyncStatus || '—'}
              </div>
              {guide.synchronization.lastSyncError && <Callout tone="danger">{guide.synchronization.lastSyncError}</Callout>}
              {guide.synchronization.schedule && (
                <CodeBlock value={{
                  sync_mode: guide.synchronization.schedule.sync_mode,
                  is_enabled: guide.synchronization.schedule.is_enabled,
                  next_run_at: guide.synchronization.schedule.next_run_at,
                  last_result: guide.synchronization.schedule.last_result,
                  consecutive_failure_count: guide.synchronization.schedule.consecutive_failure_count,
                }}
                />
              )}
              <h4 className="text-sm font-bold">Recent sync history (live)</h4>
              <div className="overflow-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-[10px] uppercase text-slate-500"><tr><th className="py-1">Sync</th><th>Status</th><th>Recv</th><th>Ins</th><th>Upd</th><th>Fail</th></tr></thead>
                  <tbody>
                    {(guide.synchronization.history || []).map((h) => (
                      <tr key={h.syncId} className="border-t border-slate-100">
                        <td className="py-1.5 font-mono">{h.syncId}</td>
                        <td>{h.status}</td>
                        <td>{h.received ?? '—'}</td>
                        <td>{h.inserted ?? '—'}</td>
                        <td>{h.updated ?? '—'}</td>
                        <td>{h.failed ?? '—'}</td>
                      </tr>
                    ))}
                    {!guide.synchronization.history?.length && <tr><td colSpan={6} className="py-3 text-slate-500">Belum ada history sync</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold" onClick={() => navigate(guide.synchronization.links.syncControl)}>View Sync Job</button>
                <button type="button" className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold" onClick={() => navigate(guide.reconciliation.links.view)}>View Reconciliation</button>
              </div>
              <h4 className="text-sm font-bold">Webhook</h4>
              <div className="text-sm">{guide.webhook?.status || 'Not Supported'} — {guide.webhook?.notes || ''}</div>
              <h4 className="text-sm font-bold">Reconciliation</h4>
              <ul className="list-disc pl-5 text-sm text-slate-600">
                {(guide.reconciliation.guide || []).map((g) => <li key={g}>{g}</li>)}
              </ul>
              {guide.reconciliation.latest && <CodeBlock value={guide.reconciliation.latest} />}
            </div>
          )}

          {section === 'testing' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold">Testing</h3>
              <Callout tone="warn">{guide.testing.note}</Callout>
              <div className="overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-[11px] uppercase text-slate-500"><tr><th className="py-2">Check</th><th>Status</th></tr></thead>
                  <tbody>
                    {(guide.testing.checklist || []).map((c) => (
                      <tr key={c.id} className="border-t border-slate-100"><td className="py-2">{c.label}</td><td><Badge value={c.status === 'Passed' ? 'Active' : c.status === 'Failed' ? 'Error' : 'Not Configured'} /></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="rounded-md bg-teal-700 px-3 py-2 text-sm font-semibold text-white" onClick={() => navigate(guide.testing.playgroundLink)}>
                Buka API Playground
              </button>
            </div>
          )}

          {section === 'troubleshooting' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold">Troubleshooting</h3>
              {[...(guide.troubleshooting.common || []), ...(guide.troubleshooting.member || [])].map((issue) => (
                <div key={issue.id || issue.symptom} className="rounded-lg border border-slate-100 p-3">
                  <div className="font-semibold text-sm text-slate-900">{issue.symptom}</div>
                  <div className="mt-2 text-xs text-slate-600"><span className="font-bold">Possible cause:</span> {(issue.possibleCauses || []).join('; ')}</div>
                  <div className="mt-1 text-xs text-slate-600"><span className="font-bold">Verify:</span> {(issue.verify || []).join('; ')}</div>
                  <div className="mt-1 text-xs text-slate-600"><span className="font-bold">Action:</span> {issue.action}</div>
                  <div className="mt-1 text-[11px] text-slate-400">Related: {(issue.related || []).join(', ')}</div>
                </div>
              ))}
            </div>
          )}

          {section === 'go-live' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-base font-bold flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Go-Live Checklist</h3>
              <Callout tone="warn">{guide.goLive.checklist?.productionReadyClaim}</Callout>
              {Object.entries(guide.goLive.checklist || {}).filter(([k]) => k !== 'productionReadyClaim').map(([group, items]) => (
                <div key={group}>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{group}</div>
                  <ul className="space-y-2">
                    {(items || []).map((item) => (
                      <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 px-3 py-2 text-sm">
                        <span>{item.label}</span>
                        <Badge value={item.status === 'Verified' ? 'Active' : 'Not Configured'} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {(guide.goLive.extras || []).map((x) => <Callout key={x}>{x}</Callout>)}
              <div>
                <div className="mb-2 text-sm font-bold">Developer Portal Readiness</div>
                <ul className="list-disc pl-5 text-sm text-slate-600">
                  {((guide.apiPublication || guide.ibm || {}).readinessChecklist || []).map((c) => <li key={c}>{c}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MemberIntegrationGuide() {
  const navigate = useNavigate();
  const { memberCode } = useParams();
  const [searchParams] = useSearchParams();
  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [platform, setPlatform] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const code = memberCode || searchParams.get('member');

  React.useEffect(() => {
    if (code) return undefined;
    setLoading(true);
    channelApi.get('/admin/integration-guides')
      .then((res) => {
        setItems(Array.isArray(res.data?.data) ? res.data.data : []);
        setSummary(res.data?.meta?.summary || null);
        setPlatform(res.data?.meta?.platform || null);
      })
      .catch((e) => setError(e.response?.data?.message || e.message || 'Gagal memuat directory'))
      .finally(() => setLoading(false));
    return undefined;
  }, [code]);

  if (code) return <GuideDetail code={String(code).toUpperCase()} />;

  return (
    <div className="space-y-4">
      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="flex justify-end">
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold"
          onClick={async () => {
            const res = await channelApi.get('/admin/integration-guides/reference-export');
            const blob = new Blob([JSON.stringify(res.data?.data || {}, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'prime-provider-reference-all.json';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Download className="h-4 w-4" /> Provider References (not Gateway POC)
        </button>
      </div>
      <Directory
        items={items}
        summary={summary}
        platform={platform}
        loading={loading}
        onOpen={(c) => navigate(`/admin/internal-tools/api-documentation/${c}?tab=guide`)}
      />
    </div>
  );
}
